import { colors } from 'styles/theme/colors';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { styledTheme } from 'styles/theme/theme';
import { AsYouType, type CountryCode as LibCountryCode } from 'libphonenumber-js';
import { additionalCountryCodeMap, type CountryCode } from 'components/inputs/CountrySelect';

export const handleResponse = async (res: Response) => {
  const data = await res.json();

  return res.ok ? data : Promise.reject(data);
};

export const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return str ?? '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getLinkedColor = (linkedTo: string) => {
  switch (linkedTo) {
    case 'Style':
      return {
        color: styledTheme.palette.text.link,
        deleteIconColor: styledTheme.palette.utility.marineBlue[400],
        borderColor: styledTheme.palette.utility.marineBlue[400],
      };
    case 'Item':
      return {
        color: styledTheme.palette.utility.lavender[800],
        deleteIconColor: styledTheme.palette.utility.lavender[600],
        borderColor: styledTheme.palette.utility.lavender[600],
      };
    case 'Sampling':
      return {
        color: styledTheme.palette.text.brandSecondary,
        deleteIconColor: styledTheme.palette.border.brand,
        borderColor: styledTheme.palette.border.brand,
      };
    case 'Certificate':
      return {
        color: styledTheme.palette.text.warning,
        deleteIconColor: styledTheme.palette.border.warning,
        borderColor: styledTheme.palette.border.warning,
      };
  }
};

export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'Low':
      return styledTheme.palette.bg.infoPrimary;
    case 'Medium':
      return styledTheme.palette.bg.infoSecondary;
    case 'High':
      return styledTheme.palette.bg.infoSolid;
    default:
      return styledTheme.palette.bg.infoPrimary;
  }
};
export const getStateColor = (state: string) => {
  switch (state) {
    case 'To Do':
      return colors.grey[600];
    case 'In Progress':
      return colors.warning[400];
    case 'Stuck':
      return colors.error[500];
    case 'Done':
      return colors.success[500];
  }
};
export const formatDateTime = (dateString: string | undefined, dateFormat: string) => {
  if (!dateString) return '';
  // A bare date (e.g. "2026-06-08", no time component) is parsed as UTC midnight by Date.parse,
  // then formatted in the viewer's local time — for any timezone west of UTC that renders as the
  // previous day. Force local-midnight parsing so the displayed date always matches the input.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = Date.parse(isDateOnly ? `${dateString}T00:00:00` : dateString);
  if (isNaN(parsed)) {
    return '';
  }
  return format(parsed, dateFormat);
};

/**
 * Encodes a value with base64 if it contains special characters that need special handling.
 * Uses URL-safe base64 encoding (replaces + with - and / with _) to avoid URL encoding issues.
 * Uses "b64-" prefix instead of "b64:" to avoid needing URL encoding of the colon.
 * Otherwise returns the value as-is (normal URL encoding will be applied by URLSearchParams).
 *
 * @param value - The string value to encode
 * @returns The encoded value with "b64-" prefix if special characters are present, otherwise the original value
 */
export const encodeValueWithBase64 = (value: string | null | undefined): string => {
  if (!value) return value || '';
  const specialChars = ['<', '>', '&', '*', '%', '$', ';', '"'];
  if (specialChars.some(char => value.includes(char))) {
    const utf8Binary = Array.from(new TextEncoder().encode(value), b =>
      String.fromCharCode(b)
    ).join('');
    const base64 = btoa(utf8Binary)
      .replace(/\+/g, '-') // Replace + with - for URL-safe base64
      .replace(/\//g, '_') // Replace / with _ for URL-safe base64
      .replace(/=/g, ''); // Remove padding = for URL-safe base64
    return `b64-${base64}`; // Use - instead of : to avoid URL encoding
  }
  return value;
};

/** Same rules as PO path segments in {@link formatUrlWithPoRefNumber}. */
const poPathSegmentUsesBase64 = (poId: string) =>
  ['<', '>', '&', '%', '$', ';', '"', '*'].some(char => poId.includes(char));

/**
 * Single path segment for a PO id in React Router and in `/api/.../purchase-orders/{poId}/...`.
 * Keeps browser URLs aligned with API URLs built by {@link stringParse}.
 *
 * `PurchaseOrderData.id` is typed `string`, but coerce defensively: a caller one level up
 * (`PurchaseOrder.tsx`'s row-click handler) passes the id straight through with no guarantee the
 * value is actually a string at runtime — a plain numeric id (e.g. an `int` primary key
 * serialized as a JSON number) reaches here as a `number` and `poId.includes(...)` throws
 * (`poId.includes is not a function`), silently aborting the click handler before `navigate()`
 * ever runs — the row looks clickable but does nothing. Reproduced while writing DL-62767's
 * `contact-person-payload.spec.ts` (a MirageJS-created purchase order's `id` is a raw `number`).
 * `String(poId)` is a no-op for the already-correct string case and only changes behavior for
 * the previously-crashing numeric case, so this is a safe, backward-compatible fix either way.
 */
export const encodePurchaseOrderIdForPath = (poId: string | number | null | undefined): string => {
  if (poId == null || poId === '') return '';
  const id = String(poId);
  if (poPathSegmentUsesBase64(id)) {
    return encodeURIComponent(encodeValueWithBase64(id));
  }
  return encodeURIComponent(id);
};

export const stringParse = (text: string, params?: string[]) => {
  // replace all {0}, {1}, {2} with the params
  if (!params) {
    return text;
  }

  // Check if this is a purchase-orders endpoint and delegate to specialized function
  if (
    text?.toLowerCase()?.includes('/purchase-orders/') ||
    text?.toLowerCase()?.includes('/purchase-order/')
  ) {
    // Dynamically detect which placeholder index is the PO refNumber by searching for the pattern
    // /purchase-orders/{X}/ or /purchase-order/{X}/ in the URL template.
    const poRefNumberIndices: number[] = [];
    // App: `/purchase-orders/{org}/{po}/...` — PO id is {1}, not {0}
    if (text?.match(/\/purchase-orders\/\{0\}\/\{1\}/i)) {
      poRefNumberIndices.push(1);
    } else {
      // API: `.../purchase-orders/{poId}/{poOrgId}/...` — first segment after /purchase-orders/
      const poThenOrg = text?.match(/\/purchase-orders\/\{(\d+)\}\/\{\d+\}/i);
      if (poThenOrg) {
        poRefNumberIndices.push(parseInt(poThenOrg[1], 10));
      } else {
        const poPattern = /\/purchase-orders?\/\{(\d+)\}/gi;
        let match;
        while ((match = poPattern.exec(text)) !== null) {
          const index = parseInt(match[1], 10);
          poRefNumberIndices.push(index);
        }

        if (poRefNumberIndices.length === 0) {
          poRefNumberIndices.push(1);
        }
      }
    }

    return formatUrlWithPoRefNumber(text, params, poRefNumberIndices);
  }

  // For non-PO endpoints, use standard URL encoding
  return text?.replace(/{(\d+)}/g, (match, number) => {
    return typeof params[number] !== 'undefined' ? encodeURIComponent(params[number]) : match;
  });
};

export const formatUrlWithPoRefNumber = (
  text: string,
  params?: string[],
  poRefNumberIndices: number[] = []
) => {
  if (!params) {
    return text;
  }
  return text?.replace(/{(\d+)}/g, (match, number, offset) => {
    if (typeof params[number] !== 'undefined') {
      const paramIndex = Number(number);
      const isPoRefNumber = poRefNumberIndices.includes(paramIndex);

      // useParams() already decodes path segments once; do not decode %XX again (breaks literal "%20" in refs).
      const decoded = params[number];

      // Check if this placeholder is in the query string portion (after ? or &)
      const textBeforeMatch = text.substring(0, offset);
      const isInQueryString = textBeforeMatch.includes('?');

      if (isInQueryString) {
        // For query parameters, check if it's a refNumber parameter name or explicitly marked as PO refNumber
        const paramBeforeMatch = text.substring(Math.max(0, offset - 20), offset);
        const isRefNumberParam = paramBeforeMatch.toLowerCase().includes('refnumber');

        if (isPoRefNumber || isRefNumberParam) {
          return encodeValueWithBase64(decoded);
        }
        // URL-encode other query parameters to handle special characters
        return encodeURIComponent(decoded);
      }

      if (isPoRefNumber) {
        return encodePurchaseOrderIdForPath(decoded);
      }

      // Default: URL-encode for other path segments
      return encodeURIComponent(decoded);
    }
    return match;
  });
};

export const getKeyByValue = (obj: { [key: number]: string }, value: string) => {
  return Number(Object.keys(obj).find(key => obj[Number(key)] === value));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isValueInArray(arr: any[], value: any): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function deepEqual(a: any, b: any): boolean {
    if (a === b) return true; // Same reference or primitive value
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(b, key) || !deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }

  return arr.some(item => {
    if (typeof item === 'object' && item !== null) {
      if (Array.isArray(item)) {
        return Array.isArray(value) ? deepEqual(item, value) : false;
      }
      return deepEqual(item, value);
    }
    return item === value;
  });
}

export const getPhoneNumberWithDetails = (
  phoneNumber?: string,
  countryCode?: string | CountryCode
) => {
  const mappedCountryCode = additionalCountryCodeMap?.[countryCode as string] ?? countryCode;

  const phoneNumberDetails = new AsYouType(mappedCountryCode as LibCountryCode);
  const rawInput = phoneNumber ?? '';
  const formattedInput = phoneNumberDetails.input(rawInput);
  const number = phoneNumberDetails.getNumber();

  return {
    number: number?.number ?? formattedInput,
    country: number?.country ?? countryCode,
    national: number?.formatNational?.() ?? formattedInput,
    international: number?.formatInternational?.() ?? formattedInput,
    uri: number?.getURI?.() ?? null,
    type: number?.getType?.() ?? '',
    possible: number?.isPossible() ? 'true' : 'false',
    valid: number?.isValid() ? 'true' : 'false',
  };
};

export const organizationType = {
  Designer: 1,
  Supplier: 2,
};

export const trim = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.trim();
};

export const DefaultDate = '0001-01-01T00:00:00';

/**
 * Converts a date into a human-readable relative time string.
 * Returns an empty string for null, undefined, or invalid dates like '0001-01-01T00:00:00'.
 */
export const getTimeAgoString = (date?: string | Date | number | null): string => {
  if (!date || date === DefaultDate || (typeof date === 'string' && date.trim() === '')) {
    return '';
  }

  let parsedDate: Date;

  if (typeof date === 'string') {
    parsedDate = parseISO(date);
  } else {
    parsedDate = new Date(date);
  }

  if (!isValid(parsedDate)) return '';

  return formatDistanceToNow(parsedDate, { addSuffix: true });
};
